/**
 * Results Grouped View - Collapsible grouped view for evaluation results
 */

import type { GroupBy } from './ResultsFilterBar';
import {
  getRepairStatus,
  getStageStatus,
  getStatusIndicator,
  type StageStatus,
} from './StageStatusCell';

interface EvalResult {
  id: string;
  prompt_id: string;
  model_config_id: string;
  is_valid_json: boolean;
  error?: string;
  execution_time_ms?: number;
  created_at: string;
  prompt?: any;
  model_config?: any;
  eval_run?: any;
  evaluation_ok?: boolean;
  formula_errors?: boolean;
  api_call_error?: string | null;
  json_parse_error?: string | null;
  schema_validation_error?: any[] | null;
  repair_error?: string | null;
  formula_evaluation_error?: string | null;
}

interface ResultGroup {
  key: string;
  label: string;
  items: EvalResult[];
  validCount: number;
  totalCount: number;
  run: any;
}

interface ResultsGroupedViewProps {
  groups: ResultGroup[];
  groupBy: GroupBy;
  onResultClick: (resultId: string) => void;
}

function formatTimestamp(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatExecutionTime(ms: number | undefined): string {
  if (!ms) return '—';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${seconds}s`;
}

function getStatusColor(status: StageStatus): string {
  switch (status) {
    case '✓':
      return '#2E7D4A';
    case '✗':
      return '#C41E3A';
    case '⚠':
      return '#B8860B';
    default:
      return '#A0A0A0';
  }
}

/** Get display name for a prompt, falling back to truncated text if name is missing */
function getPromptDisplayName(prompt: any): string {
  if (prompt?.name) return prompt.name;
  if (prompt?.text) {
    const truncated = prompt.text.slice(0, 40);
    return truncated.length < prompt.text.length
      ? `${truncated}...`
      : truncated;
  }
  return 'Unknown';
}

export function ResultsGroupedView({
  groups,
  groupBy,
  onResultClick,
}: ResultsGroupedViewProps) {
  return (
    <div className="grid gap-6">
      {groups.map(group => (
        <details
          key={group.key}
          className="bg-white rounded-xl overflow-hidden"
          style={{ border: '1px solid #E8E0D8' }}
          open
        >
          <summary
            className="px-6 py-5 cursor-pointer flex justify-between items-center"
            style={{ background: '#F5F0EB', borderBottom: '1px solid #E8E0D8' }}
          >
            <div className="flex-1">
              <h3
                className="text-lg font-semibold m-0"
                style={{ color: '#1A1A1A' }}
              >
                {group.label}
              </h3>
              <div className="text-sm mt-1" style={{ color: '#6B6B6B' }}>
                {group.run ? (
                  <>
                    {group.run.completed_executions} completed,{' '}
                    {group.run.failed_executions} failed
                    {group.run.started_at &&
                      ` • Started ${formatTimestamp(group.run.started_at)}`}
                  </>
                ) : (
                  `${group.validCount} / ${group.totalCount} successful`
                )}
              </div>
            </div>
            <div className="text-sm text-right" style={{ color: '#A0A0A0' }}>
              {group.run ? (
                <>
                  <div className="font-semibold">
                    {Math.round(
                      (group.run.completed_executions /
                        group.run.total_executions) *
                        100
                    )}
                    % complete
                  </div>
                  <div className="text-xs mt-1">
                    {group.run.total_executions} total executions
                  </div>
                </>
              ) : (
                `${Math.round((group.validCount / group.totalCount) * 100)}% success rate`
              )}
            </div>
          </summary>

          <div className="p-4 overflow-x-auto">
            <table
              className="w-full text-sm"
              style={{ borderCollapse: 'collapse' }}
            >
              <thead>
                <tr style={{ borderBottom: '2px solid #E8E0D8' }}>
                  <th
                    className="px-2 py-2 text-left font-semibold text-xs"
                    style={{ color: '#1A1A1A' }}
                  >
                    Status
                  </th>
                  <th
                    className="px-2 py-2 text-left font-semibold text-xs"
                    style={{ color: '#1A1A1A' }}
                  >
                    {groupBy === 'prompt' ? 'Config' : 'Prompt'}
                  </th>
                  <th
                    className="px-2 py-2 text-left font-semibold text-xs"
                    style={{ color: '#1A1A1A' }}
                  >
                    Model
                  </th>
                  <th
                    className="px-2 py-2 text-center font-semibold text-xs"
                    style={{ color: '#1A1A1A' }}
                  >
                    Time
                  </th>
                  <th
                    className="px-2 py-2 text-center font-semibold text-xs"
                    style={{ color: '#1A1A1A' }}
                  >
                    API
                  </th>
                  <th
                    className="px-2 py-2 text-center font-semibold text-xs"
                    style={{ color: '#1A1A1A' }}
                  >
                    Parse
                  </th>
                  <th
                    className="px-2 py-2 text-center font-semibold text-xs"
                    style={{ color: '#1A1A1A' }}
                  >
                    Schema
                  </th>
                  <th
                    className="px-2 py-2 text-center font-semibold text-xs"
                    style={{ color: '#1A1A1A' }}
                  >
                    Repair
                  </th>
                  <th
                    className="px-2 py-2 text-center font-semibold text-xs"
                    style={{ color: '#1A1A1A' }}
                  >
                    Result
                  </th>
                  <th
                    className="px-2 py-2 text-left font-semibold text-xs"
                    style={{ color: '#1A1A1A' }}
                  >
                    Timestamp
                  </th>
                </tr>
              </thead>
              <tbody>
                {group.items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="py-8 text-center italic"
                      style={{ color: '#A0A0A0' }}
                    >
                      No results yet. Executions are running in the background.
                    </td>
                  </tr>
                ) : (
                  group.items.map(result => {
                    const status = getStatusIndicator(result);
                    const apiStatus = getStageStatus(result, 'api_call');
                    const parseStatus = getStageStatus(result, 'json_parse');
                    const schemaStatus = getStageStatus(
                      result,
                      'schema_validation'
                    );
                    const repairStatus = getRepairStatus(result);

                    return (
                      <tr
                        key={result.id}
                        onClick={() => onResultClick(result.id)}
                        className="cursor-pointer transition-colors duration-150"
                        style={{ borderBottom: '1px solid #E8E0D8' }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background =
                            'rgba(139, 35, 50, 0.04)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <td
                          className="px-2 py-2 text-center text-lg"
                          title={status.label}
                        >
                          {status.icon}
                        </td>
                        <td
                          className="px-2 py-2 font-medium max-w-xs truncate"
                          style={{ color: '#1A1A1A' }}
                          title={
                            groupBy !== 'prompt'
                              ? result.prompt?.text
                              : undefined
                          }
                        >
                          {groupBy === 'prompt'
                            ? result.model_config?.name || 'Unknown'
                            : getPromptDisplayName(result.prompt)}
                        </td>
                        <td
                          className="px-2 py-2 text-xs"
                          style={{ color: '#6B6B6B' }}
                        >
                          {result.model_config?.provider}/
                          {result.model_config?.api_model || '—'}
                        </td>
                        <td
                          className="px-2 py-2 text-center"
                          style={{ color: '#A0A0A0' }}
                        >
                          {formatExecutionTime(result.execution_time_ms)}
                        </td>
                        <td
                          className="px-2 py-2 text-center"
                          style={{ color: getStatusColor(apiStatus) }}
                        >
                          {apiStatus}
                        </td>
                        <td
                          className="px-2 py-2 text-center"
                          style={{ color: getStatusColor(parseStatus) }}
                        >
                          {parseStatus}
                        </td>
                        <td
                          className="px-2 py-2 text-center"
                          style={{ color: getStatusColor(schemaStatus) }}
                        >
                          {schemaStatus}
                        </td>
                        <td
                          className="px-2 py-2 text-center"
                          style={{ color: getStatusColor(repairStatus) }}
                        >
                          {repairStatus}
                        </td>
                        <td
                          className="px-2 py-2 text-center"
                          style={{
                            color:
                              result.evaluation_ok === true
                                ? '#2E7D4A'
                                : result.evaluation_ok === false
                                  ? '#C41E3A'
                                  : '#A0A0A0',
                          }}
                        >
                          {result.evaluation_ok === true
                            ? '✓'
                            : result.evaluation_ok === false
                              ? '✗'
                              : '—'}
                        </td>
                        <td
                          className="px-2 py-2 text-xs whitespace-nowrap"
                          style={{ color: '#A0A0A0' }}
                        >
                          {formatTimestamp(result.created_at)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </details>
      ))}
    </div>
  );
}
